class PaymentOptions extends HTMLElement {
  KLARNA_TAG = 'klarna-placement';
  AFTERPAY_TAG = 'square-placement';
  SHOPPAY_TAG = 'shopify-payment-terms';

  /** @type {ShadowRoot} */
  shadow;

  /** @type {string} */
  currentVariantId;

  /** @type {number} */
  currentVariantPrice;

  /** @type {string} */
  baseStyles;

  /**
   * @typedef {{ enabled: boolean; installments: number | null; pricePerInstallment: string | null }} InstallmentOption
   */

  /**
   * @typedef {{ enabled: boolean; installments: number | null; pricePerInstallment: string | null; minPrice?: string; maxPrice?: string; isProductPriceTooLow?: boolean; isProductPriceTooHigh?: boolean }} ShopPayOption
   */

  /**
   * @typedef {{ klarna: InstallmentOption; afterpay: InstallmentOption; shoppay: ShopPayOption }} AvailablePaymentOptions
   */

  /** @type {AvailablePaymentOptions} */
  availablePaymentOptions;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });

    this.currentVariantId = this.getAttribute('current-variant-id') || '';
    this.currentVariantPrice = this.parsePrice(this.getAttribute('current-variant-price') || '0');

    this.availablePaymentOptions = /** @type {PaymentOptions['availablePaymentOptions']} */ ({
      klarna: {
        enabled: false,
        installments: null,
        pricePerInstallment: null,
      },
      afterpay: {
        enabled: false,
        installments: null,
        pricePerInstallment: null,
      },
      shoppay: {
        enabled: false,
        installments: null,
        pricePerInstallment: null,
      },
    });

    this.baseStyles = `
      <style>
        .payment-options {
          min-height: 33px;
          filter: greyscale(1);
        }

        .klarna-button,
        .afterpay-button,
        .shop-pay-button {
          cursor: pointer;
          vertical-align: middle;
          background-color: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
          margin: 0;
        }

        .klarna-button img,
        .afterpay-button img,
        .shop-pay-button img {
          width: 100%;
          height: auto;
        }
      </style>
    `;

    this.shadow.innerHTML = this.baseStyles;
  }

  get klarna() {
    return this.getProviderElement(this.KLARNA_TAG);
  }

  get afterPay() {
    return this.getProviderElement(this.AFTERPAY_TAG);
  }

  get shopPay() {
    return this.getProviderElement(this.SHOPPAY_TAG);
  }

  /**
   * Finds provider elements close to this component first, then falls back to document lookup.
   * @param {string} selector
   */
  getProviderElement(selector) {
    const parent = this.parentElement;
    if (parent) {
      const localMatch = parent.querySelector(selector);
      if (localMatch) return localMatch;
    }

    const section = this.closest('[id^="shopify-section-"]');
    if (section) {
      const sectionMatch = section.querySelector(selector);
      if (sectionMatch) return sectionMatch;
    }

    return document.querySelector(selector);
  }

  async connectedCallback() {
    // Render a fallback immediately so the UI isn't blank while third-party widgets load.
    this.render();

    // Try an immediate sync for whichever providers are already available.
    try {
      this.updatePaymentOptions();
      this.render();
    } catch (e) {
      // Keep fallback if parsing fails
    }

    this.observeInjectedComponents();
  }

  observeInjectedComponents() {
    const syncPaymentOptions = () => {
      try {
        this.updatePaymentOptions();
        this.render();
      } catch (e) {
        // Keep fallback if parsing fails
      }
    };

    const observer = new MutationObserver(() => {
      syncPaymentOptions();
    });

    // Start watching document body for injected elements and Shop Pay metadata updates
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['shopify-meta'],
    });

    // Run once after observer starts to catch already-inserted widgets.
    syncPaymentOptions();

    // Don't observe forever if integrations never load
    setTimeout(() => observer.disconnect(), 10000);
  }

  areComponentsReady() {
    const klarnaEl = /** @type {any} */ (this.klarna);
    const afterPayEl = /** @type {any} */ (this.afterPay);

    // Many third-party widgets use closed shadow DOM; don't rely on `shadowRoot`.
    const hasKlarnaData = !!klarnaEl?.placementAttributes?.mainText;
    const hasAfterPayApi = typeof afterPayEl?.getMainText === 'function';

    return hasKlarnaData && hasAfterPayApi;
  }

  updatePaymentOptions() {
    this.updateKlarnaTerms();
    this.updateAfterPayTerms();
    this.updateShopPayTerms();
  }

  /**
   * Build the template for the payment options based on the available payment options
   *   Klarna, AfterPay and ShopPay (4 payments)
   *   4 interest-free payments of $X.XX with <afterpay>, <klarna>, or <ShopPay>

   *   Klarna and AfterPay (4 payments). ShopPay (12 installments with interest)
   *   4 interest-free payments of $X.XX with <afterpay> and <klarna>, or from $X.XX/mo. with <ShopPay>

   *   Klarna and AfterPay (4 payments)
   *   4 interest-free payments of $X.XX with <afterpay> or <klarna>
   
   *   Klarna and AfterPay (12 installments with interest)
   *   from $X.XX/mo. with <afterpay> or from $X.XX/mo. with <klarna>

   *   Non-USA (International)
   *   Pay with interest-free installments with <aftepay> or <klarna>
   */
  buildTemplate() {
    // Figure out which scenario we’re in
    const scenario = this.computeScenario();
    // Return the appropriate template
    return this.buildMessage(scenario);
  }

  computeScenario() {
    const {
      klarna: { enabled: kEnabled, installments: kInst },
      afterpay: { enabled: aEnabled, installments: aInst },
      shoppay: { enabled: sEnabled, installments: sInst, isProductPriceTooLow: isShopPayProductPriceTooLow },
    } = this.availablePaymentOptions;

    const country = Shopify?.country;

    // If country is explicitly known and not US, return the international scenario.
    // If country is missing/undefined, don't force INTERNATIONAL fallback.
    if (country && country !== 'US') return 'INTERNATIONAL';

    // IF Klarna, Afterpay and ShopPay are enabled
    if (kEnabled && aEnabled && sEnabled) {
      if (kInst === 4 && aInst === 4 && sInst === 4) {
        return 'K-A-SH#4';
      }

      if (kInst === 4 && aInst === 4 && sInst !== 2 && sInst !== 4) {
        return 'K-A#4__SH#INTEREST';
      }

      if (kInst === 4 && aInst === 4 && sInst === 2) {
        return 'K-A#4__SH#2';
      }
    }

    // IF Klarna and Afterpay are enabled but ShopPay is not
    if (kEnabled && aEnabled) {
      if (isShopPayProductPriceTooLow) {
        return 'K-A#4__SH#TOOLOW';
      }

      if (kInst === 4 && aInst === 4) {
        return 'K-A#4';
      }
    }

    return 'FALLBACK';
  }

  /** @param {string} scenario */
  buildMessage(scenario) {
    const shopPayPrice = this.availablePaymentOptions.shoppay.pricePerInstallment;
    const shopPayMinPrice = this.availablePaymentOptions.shoppay.minPrice;

    const afterPayPrice = this.availablePaymentOptions.afterpay.pricePerInstallment;
    const klarnaPrice = this.availablePaymentOptions.klarna.pricePerInstallment;

    switch (scenario) {
      case 'K-A-SH#4':
        return `
            <div class="payment-options">
              4 interest-free payments of ${shopPayPrice} with <afterpay> <klarna> or <shopPay>
            </div>
          `;
      case 'K-A#4':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> or <klarna>
            </div>
          `;
      case 'K-A#4__SH#INTEREST':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> and <klarna>
              or from ${shopPayPrice}/mo. with <shopPay>
            </div>
          `;
      case 'K-A#4__SH#2':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> or <klarna> or 2 interest-free installments of ${shopPayPrice} with <shopPay>
            </div>`;
      case 'K-A#4__SH#TOOLOW':
        return `
            <div class="payment-options">
              4 interest-free payments of ${afterPayPrice} with <afterpay> or <klarna> or pay over time for orders over ${shopPayMinPrice} with <shopPay>
            </div>
          `;
      case 'INTERNATIONAL':
        return `
            <div class="payment-options">
              Pay with interest-free installments with <afterpay> or <klarna>
            </div>
          `;
      default:
        return `
            <div class="payment-options">
              Pay with interest-free installments with <afterpay> or <klarna>
            </div>
          `;
    }
  }

  /** @param {string} paymentOption */
  getPaymentLogo(paymentOption) {
    switch (paymentOption) {
      case this.KLARNA_TAG:
        return `
            <button id="payment-options-klarna-button" class="klarna-button" style="width: 50px">
              <img src="https://cdn.shopify.com/s/files/1/0747/5039/files/klarna-logo.png" alt="" />
            </button>`;
      case this.AFTERPAY_TAG:
        return `
            <button id="payment-options-afterpay-button" class="afterpay-button" style="width: 80px">
              <img src="https://cdn.shopify.com/s/files/1/0747/5039/files/afterpay-logo.svg" alt="" />
            </button>`;
      case this.SHOPPAY_TAG:
        return `
            <button id="payment-options-shop-pay-button" class="shop-pay-button" style="width: 60px">
              <img src="https://cdn.shopify.com/s/files/1/0747/5039/files/shopPay_logo.png" alt="" />
            </button>`;
      default:
        return '';
    }
  }

  updateKlarnaTerms() {
    const klarnaEl = /** @type {any} */ (this.klarna);
    const mainText = klarnaEl?.placementAttributes?.mainText;
    if (!mainText) return;
    // Usually it returns something like '4 interest-free payments of $42.00 with'
    // '4 interest-free payments of $42.00 with'

    const price = this.getPriceFromText(mainText);
    const installments = this.getInstallmentsFromText(mainText);

    this.availablePaymentOptions.klarna.enabled = true;
    this.availablePaymentOptions.klarna.installments = installments;
    this.availablePaymentOptions.klarna.pricePerInstallment = price;
  }

  updateAfterPayTerms() {
    const afterPayEl = /** @type {any} */ (this.afterPay);
    const mainText = afterPayEl?.getMainText?.();
    if (!mainText) return;
    // Usually it returns something like 'or 4 interest-free payments of $42.00 with'
    // We need to extract the price and the number of installments

    const price = this.getPriceFromText(mainText);
    const installments = this.getInstallmentsFromText(mainText);

    this.availablePaymentOptions.afterpay.enabled = true;
    this.availablePaymentOptions.afterpay.installments = installments;
    this.availablePaymentOptions.afterpay.pricePerInstallment = price;
  }

  updateShopPayTerms() {
    if (!this.shopPay) return;

    const rawMeta = this.shopPay.getAttribute('shopify-meta');
    if (!rawMeta) return;
    /** @type {any} */
    let meta;

    try {
      meta = JSON.parse(rawMeta);
    } catch (_error) {
      return;
    }

    if (!meta || !Array.isArray(meta.variants)) return;

    const currentVariant = meta.variants?.find(
      (/** @type {any} */ variant) => String(variant.id) === String(this.currentVariantId)
    );
    const selectedVariant = currentVariant || meta.variants?.[0];

    this.availablePaymentOptions.shoppay.enabled = Boolean(selectedVariant?.eligible);
    this.availablePaymentOptions.shoppay.minPrice = meta.min_price;
    this.availablePaymentOptions.shoppay.maxPrice = meta.max_price;

    if (this.currentVariantPrice < this.parsePrice(meta.min_price)) {
      this.availablePaymentOptions.shoppay.isProductPriceTooLow = true;
      return;
    }

    if (this.currentVariantPrice > this.parsePrice(meta.max_price)) {
      this.availablePaymentOptions.shoppay.isProductPriceTooHigh = true;
      return;
    }

    let isEligibleFor12Months = false;
    let aPr = 0;

    (meta.financing_plans || []).forEach((/** @type {any} */ plan) => {
      if (
        this.currentVariantPrice >= this.parsePrice(plan.min_price) &&
        this.currentVariantPrice <= this.parsePrice(plan.max_price)
      ) {
        (plan.terms || []).forEach((/** @type {any} */ term) => {
          if (term.installments_count === 12) {
            isEligibleFor12Months = true;
            aPr = term.apr;
          }
        });
      }
    });

    if (isEligibleFor12Months) {
      this.availablePaymentOptions.shoppay.installments = 12;

      const calculatedInstallment = this.calculateInstallment(this.currentVariantPrice, aPr, 12);
      this.availablePaymentOptions.shoppay.pricePerInstallment = this.formatPrice(calculatedInstallment);
    } else {
      this.availablePaymentOptions.shoppay.installments = selectedVariant?.number_of_payment_terms || null;
      this.availablePaymentOptions.shoppay.pricePerInstallment = selectedVariant?.price_per_term || null;
    }
  }

  /** @param {string} price */
  parsePrice(price) {
    const raw = String(price).trim();
    const compact = raw.replace(/\s/g, '').replace(/[^0-9,.-]/g, '');

    let normalized = compact;

    if (normalized.includes(',') && normalized.includes('.')) {
      normalized = normalized.replace(/,/g, '');
    } else if (normalized.includes(',') && !normalized.includes('.')) {
      normalized = normalized.replace(',', '.');
    }

    return parseFloat(normalized) || 0;
  }

  /** @param {number} price */
  formatPrice(price) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    return formatter.format(price);
  }

  /** @param {string} text */
  getPriceFromText(text) {
    const match = text.match(/\$\d+\.\d+/);
    const price = match ? match[0] : null;
    return price;
  }

  /** @param {string} text */
  getInstallmentsFromText(text) {
    const patterns = [
      /(?:pay\s+in|interest[-\s]?free\s+payments\s+of|installments?\s+of)\s*(\d+)/i,
      /(\d+)\s*(?:interest[-\s]?free\s+payments?|installments?)/i,
    ];

    let monthCount = null;
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match?.[1]) {
        monthCount = parseInt(match[1], 10);
        break;
      }
    }

    return monthCount;
  }

  /** @param {number} price @param {number} apr @param {number} months */
  calculateInstallment(price, apr, months) {
    // Convert APR to a monthly interest rate (decimal form)
    const monthlyInterestRate = apr / 100 / 12;

    // If there's no interest (i.e., apr = 0), just return price / months
    if (monthlyInterestRate === 0) {
      return parseFloat((price / months).toFixed(2));
    }

    // Apply the standard formula for installment calculation
    const numerator = price * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, months);
    const denominator = Math.pow(1 + monthlyInterestRate, months) - 1;
    const monthlyPayment = numerator / denominator;

    // Return result rounded to two decimals
    return parseFloat(monthlyPayment.toFixed(2));
  }

  attachEventListeners() {
    const klarnaButton = this.shadow.querySelector('#payment-options-klarna-button');
    const afterpayButton = this.shadow.querySelector('#payment-options-afterpay-button');
    const shopPayButton = this.shadow.querySelector('#payment-options-shop-pay-button');

    if (klarnaButton) {
      klarnaButton.addEventListener('click', () => {
        const target = this.klarna?.shadowRoot?.querySelector?.('button.link');
        target?.dispatchEvent(new Event('click'));
      });
    }

    if (afterpayButton) {
      afterpayButton.addEventListener('click', () => {
        const afterPayGlobal = /** @type {any} */ (window).AfterPay;
        afterPayGlobal?.launchModal?.();
      });
    }


    if (shopPayButton) {
      shopPayButton.addEventListener('click', () => {
        const target = this.shopPay?.shadowRoot?.querySelector?.('#shopify-installments-cta');
        target?.dispatchEvent(new Event('click'));
      });
    }
  }

  render() {
    const template = this.buildTemplate();

    const updatedTemplate = template
      .replace('<afterpay>', this.getPaymentLogo(this.AFTERPAY_TAG))
      .replace('<klarna>', this.getPaymentLogo(this.KLARNA_TAG))
      .replace('<shopPay>', this.getPaymentLogo(this.SHOPPAY_TAG));

    // Replace (not append) to avoid duplicating markup on re-render.
    this.shadow.innerHTML = `${this.baseStyles}${updatedTemplate}`;

    if (Shopify?.country !== undefined ? Shopify.country === 'US' : true) {
      this.attachEventListeners();
    }
  }
}

if (!customElements.get('payment-options')) {
  customElements.define('payment-options', PaymentOptions);
}